/**
 * Collection Search Page Custom Element
 *
 * Fetches search results based on a collection's search_query metafield
 * and updates the section on the collection page
 */
export default class CollectionSearchPage extends HTMLElement {
  async connectedCallback() {
    const searchQuery = this.getAttribute('data-search-query');
    let sectionId = this.getAttribute('section-id');

    if (!searchQuery) {
      console.error('[CollectionSearchPage] Missing required attribute: data-search-query');
      return;
    }

    // If section-id is provided as a short name (e.g., "collection_search_results"),
    // find the full section wrapper ID from the DOM
    if (sectionId && !sectionId.startsWith('template--')) {
      const sectionWrapper = document.querySelector(`#shopify-section-${sectionId}, [id*="__${sectionId}"]`);
      if (sectionWrapper) {
        // Extract the full section ID from the wrapper
        const fullId = sectionWrapper.id.replace('shopify-section-', '');
        console.log('[CollectionSearchPage] Resolved section ID from', sectionId, 'to', fullId);
        sectionId = fullId;
      } else {
        console.error('[CollectionSearchPage] Could not find section wrapper for:', sectionId);
        return;
      }
    }

    if (!sectionId) {
      console.error('[CollectionSearchPage] Missing required attribute: section-id');
      return;
    }

    console.log('[CollectionSearchPage] Initializing with query:', searchQuery, 'section:', sectionId);

    // Update the browser URL FIRST to include the search query
    // This ensures any subsequent section rendering uses the correct search context
    this.#updateBrowserURL(searchQuery);

    // Intercept section rendering BEFORE fetching to ensure correct base URL
    await this.#interceptSectionRenderer(sectionId, searchQuery);

    // Fetch and replace the placeholder section with search results
    await this.#fetchAndInjectSearchResults(searchQuery, sectionId);
  }

  /**
   * Show the search results section
   * @param {string} sectionId - Section ID to show
   */
  #showSearchSection(sectionId) {
    const sectionWrapper = document.getElementById(`shopify-section-${sectionId}`);
    if (sectionWrapper) {
      sectionWrapper.classList.remove('collection-search-hidden');
      console.log('[CollectionSearchPage] Showing search section');
    }
  }

  /**
   * Hide the loading skeleton
   */
  #hideLoadingSkeleton() {
    const loadingElement = document.querySelector('[data-collection-search-loading]');
    if (loadingElement) {
      loadingElement.classList.add('is-hidden');
      console.log('[CollectionSearchPage] Hiding loading skeleton');
    }
  }

  /**
   * Fetch search results page and inject into collection page
   * @param {string} searchQuery - Search query to use
   * @param {string} sectionId - Section ID to fetch (the search-results section from collection template)
   */
  async #fetchAndInjectSearchResults(searchQuery, sectionId) {
    try {
      // Build search URL with the query
      const url = new URL('/search', window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', searchQuery);

      // If section ID provided, fetch just that section
      if (sectionId) {
        url.searchParams.set('section_id', sectionId);
      }

      console.log('[CollectionSearchPage] Fetching URL:', url.toString());

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch search results: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();

      // Parse response
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Extract the section content
      let contentToInject;

      if (sectionId) {
        // Extract the specific section WITH its wrapper
        const sectionSelector = `#shopify-section-${sectionId}`;
        const section = doc.querySelector(sectionSelector);
        if (section) {
          contentToInject = section.outerHTML;
        } else {
          console.warn('[CollectionSearchPage] Section not found:', sectionSelector);
          // Fallback: try to find search-page element
          contentToInject = doc.querySelector('search-page')?.outerHTML;
        }
      } else {
        // Extract the search-page element
        const searchPage = doc.querySelector('search-page');
        if (searchPage) {
          contentToInject = searchPage.outerHTML;
        }
      }

      if (!contentToInject) {
        console.error('[CollectionSearchPage] No content found in response');
        return;
      }

      console.log('[CollectionSearchPage] Replacing placeholder section with search results');

      // Replace the placeholder section with search results
      await this.#replaceSection(contentToInject, sectionId);

      console.log('[CollectionSearchPage] Section replaced and scripts executed');

      // Hide loading skeleton and show the section
      this.#hideLoadingSkeleton();
      this.#showSearchSection(sectionId);

    } catch (error) {
      console.error('[CollectionSearchPage] Error fetching search results:', error);

      // Hide loading skeleton on error
      this.#hideLoadingSkeleton();

      this.innerHTML = `
        <div style="padding: 2rem; text-align: center;">
          <p>Failed to load products. Please try again later.</p>
        </div>
      `;
    }
  }

  /**
   * Replace the placeholder section with search results
   * @param {string} htmlContent - HTML content containing the section wrapper
   * @param {string} sectionId - Section ID to replace
   */
  async #replaceSection(htmlContent, sectionId) {
    // Find the existing placeholder section
    const existingSection = document.getElementById(`shopify-section-${sectionId}`);
    if (!existingSection) {
      console.error('[CollectionSearchPage] Could not find placeholder section to replace');
      return;
    }

    // Create a temporary container
    const temp = document.createElement('div');
    temp.innerHTML = htmlContent;

    // Extract the new section
    const newSection = temp.querySelector(`#shopify-section-${sectionId}`);
    if (!newSection) {
      console.error('[CollectionSearchPage] Could not find section in fetched content');
      return;
    }

    // Extract all script tags from the new section
    const scripts = newSection.querySelectorAll('script');
    const scriptElements = Array.from(scripts);

    // Remove scripts from section so they don't get added via replaceWith
    scriptElements.forEach(script => script.remove());

    // Replace the placeholder section with the new content
    existingSection.replaceWith(newSection);
    console.log('[CollectionSearchPage] Replaced placeholder section with search results');

    // Now manually execute each script
    const scriptPromises = scriptElements.map(oldScript => {
      return new Promise((resolve, reject) => {
        const newScript = document.createElement('script');

        // Copy all attributes
        Array.from(oldScript.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });

        // Copy inline script content if any
        if (oldScript.textContent) {
          newScript.textContent = oldScript.textContent;
        }

        // Handle module loading
        if (newScript.type === 'module' || oldScript.src) {
          newScript.onload = () => {
            console.log('[CollectionSearchPage] Loaded script:', newScript.src || 'inline script');
            resolve();
          };
          newScript.onerror = (error) => {
            console.error('[CollectionSearchPage] Error loading script:', newScript.src, error);
            resolve(); // Resolve anyway to continue
          };
        } else {
          // Inline non-module scripts execute synchronously
          resolve();
        }

        // Append to the new section to execute
        newSection.appendChild(newScript);

        // If it's an inline script without src, resolve immediately
        if (!newScript.src) {
          resolve();
        }
      });
    });

    // Wait for all scripts to load
    await Promise.all(scriptPromises);

    console.log('[CollectionSearchPage] All scripts loaded and executed');

    // Give the browser a moment to upgrade custom elements
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Update the browser URL to include the search query
   * This allows filtering, sorting, and pagination to work correctly
   * @param {string} searchQuery - Search query to use
   */
  #updateBrowserURL(searchQuery) {
    try {
      // Create new URL with search query
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('q', searchQuery);
      newUrl.searchParams.set('type', 'product');

      // Use replaceState to update URL without adding to browser history
      history.replaceState(
        { collectionSearchPage: true, query: searchQuery },
        '',
        newUrl.toString()
      );

      console.log('[CollectionSearchPage] Updated browser URL to:', newUrl.toString());
    } catch (error) {
      console.error('[CollectionSearchPage] Error updating browser URL:', error);
    }
  }

  /**
   * Intercept section rendering to force using /search as base URL
   * This prevents the section renderer from trying to fetch from the collection page URL
   * @param {string} sectionId - Section ID to intercept
   * @param {string} searchQuery - Search query for the collection
   */
  async #interceptSectionRenderer(sectionId, searchQuery) {
    try {
      // Import the section renderer
      const { sectionRenderer } = await import('@theme/section-renderer');

      // Store the original getSectionHTML method
      const originalGetSectionHTML = sectionRenderer.getSectionHTML.bind(sectionRenderer);

      // Override getSectionHTML for this section
      sectionRenderer.getSectionHTML = async function(requestedSectionId, useCache = true, url) {
        // If this is a request for the search section
        if (requestedSectionId === sectionId) {
          // Create a search URL with query params
          let searchUrl;

          if (url) {
            // If URL was provided (e.g., from pagination/filter), use it as base
            searchUrl = new URL(url, window.location.origin);
            // Ensure it's using /search as path, not the collection page URL
            if (!searchUrl.pathname.startsWith('/search')) {
              const newSearchUrl = new URL('/search', window.location.origin);
              // Copy all query params from the provided URL
              searchUrl.searchParams.forEach((value, key) => {
                newSearchUrl.searchParams.set(key, value);
              });
              searchUrl = newSearchUrl;
            }
          } else {
            // No URL provided, use current browser URL params
            const currentUrl = new URL(window.location.href);
            searchUrl = new URL('/search', window.location.origin);
            // Copy all query params (includes filters, sort, search query, etc)
            currentUrl.searchParams.forEach((value, key) => {
              searchUrl.searchParams.set(key, value);
            });
          }

          console.log('[CollectionSearchPage] Intercepted section rendering, using:', searchUrl.toString());

          // Call original method with /search as base URL
          const result = await originalGetSectionHTML(requestedSectionId, useCache, searchUrl);

          // Update browser URL to match the search URL (for filters, pagination, etc)
          // But exclude section_id from the browser URL
          const newCollectionUrl = new URL(window.location.href);
          let urlChanged = false;

          // Copy all params from searchUrl except section_id
          searchUrl.searchParams.forEach((value, key) => {
            if (key !== 'section_id' && newCollectionUrl.searchParams.get(key) !== value) {
              newCollectionUrl.searchParams.set(key, value);
              urlChanged = true;
            }
          });

          // Remove any params that are in browser URL but not in searchUrl (except section_id)
          const paramsToDelete = [];
          newCollectionUrl.searchParams.forEach((value, key) => {
            if (key !== 'section_id' && !searchUrl.searchParams.has(key)) {
              paramsToDelete.push(key);
              urlChanged = true;
            }
          });
          paramsToDelete.forEach(key => newCollectionUrl.searchParams.delete(key));

          if (urlChanged) {
            history.replaceState(
              { collectionSearchPage: true, query: searchQuery },
              '',
              newCollectionUrl.toString()
            );
            console.log('[CollectionSearchPage] Updated browser URL to:', newCollectionUrl.toString());
          }

          return result;
        }

        // For other sections, use original behavior
        return originalGetSectionHTML(requestedSectionId, useCache, url);
      };

      console.log('[CollectionSearchPage] Section renderer intercepted for section:', sectionId);
    } catch (error) {
      console.error('[CollectionSearchPage] Failed to intercept section renderer:', error);
    }
  }
}

customElements.define('collection-search-page', CollectionSearchPage);
